import { useTranslation } from 'react-i18next';
import {
  faExclamationCircle,
  faShelvesEmpty,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ProductsWithoutStockSummary } from '../../models/ProductsWithoutStock';

interface ReportProps {
  summary?: ProductsWithoutStockSummary;
  className?: string;
}

export const Report = ({ summary, className = '' }: ReportProps) => {
  const { t } = useTranslation('productsWithoutStockModule');

  // Icono personalizado "Pedidos sin stock" (currentColor)
  const OrdersNoStockIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 19 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-4.5 h-4.5"
    >
      <path
        d="M13.4325 8.72952C15.6269 8.72952 17.4042 10.4583 17.4042 12.5927C17.4042 13.4748 17.0997 14.2828 16.59 14.9331L18.8373 17.1222C19.0425 17.3218 19.0425 17.6502 18.8373 17.8498C18.6553 18.0268 18.3773 18.0462 18.1721 17.9142L18.0893 17.8498L15.842 15.6607C15.1734 16.1564 14.3394 16.4558 13.4325 16.4558C11.2382 16.4558 9.46083 14.7271 9.46083 12.5927C9.46083 10.4583 11.2382 8.72952 13.4325 8.72952ZM12.0788 1C12.7639 1 13.406 1.32193 13.8032 1.86277L15.4217 4.06799C15.6765 4.41567 15.8155 4.8374 15.8155 5.26556V7.70579C15.4812 7.55126 15.1238 7.42893 14.7564 7.34201V5.639H2.04702V13.8804C2.04702 14.4502 2.52031 14.9106 3.10613 14.9106H8.40502C8.58375 15.2776 8.8055 15.6253 9.06366 15.9407H3.10613L2.891 15.9311C1.89477 15.8313 1.10043 15.0619 1.00114 14.0929L0.991211 13.8836V5.272C0.991211 4.89535 1.09712 4.52835 1.29571 4.20964L1.38507 4.07443L3.00353 1.86921C3.35105 1.39597 3.88723 1.09014 4.47305 1.02253L4.7279 1.00644H12.0788V1ZM13.4325 9.7597C11.824 9.7597 10.5199 11.0281 10.5199 12.5927C10.5199 14.1573 11.824 15.4257 13.4325 15.4257C15.041 15.4257 16.3451 14.1573 16.3451 12.5927C16.3451 11.0281 15.041 9.7597 13.4325 9.7597ZM4.72459 2.03017C4.38369 2.03017 4.06265 2.19114 3.86406 2.46156L2.28863 4.60561H14.5115L12.936 2.46156C12.7606 2.22333 12.4959 2.07202 12.2013 2.03661L12.0755 2.03017H4.72459Z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <div className={`mb-6 ${className}`}>
      {/* Título del Reporte */}
      <h2 className="text-xl font-semibold text-title mb-4">
        {t('report.title')}
      </h2>

      {/* Grid de Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {/* Tarjeta "Con problemas" */}
        <div className="bg-card-bg border border-card-border rounded-lg p-4">
          <div className="flex items-center justify-between sm:mb-2">
            <span className="sm:text-lg font-medium text-xs">
              {t('report.withProblems')}
            </span>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="w-4.5 h-4.5 text-lines"
            />
          </div>
          <div className="text-lg sm:text-2xl font-semibold text-title">
            {summary?.total_products || 0}
          </div>
        </div>

        {/* Tarjeta "Total sin stock" */}
        <div className="bg-card-bg border border-card-border rounded-lg p-4">
          <div className="flex items-center justify-between sm:mb-2">
            <span className="sm:text-lg font-medium text-xs">
              {t('report.totalOutOfStock')}
            </span>
            <FontAwesomeIcon
              icon={faShelvesEmpty}
              className="w-4.5 h-4.5 text-lines"
            />
          </div>
          <div className="text-lg sm:text-2xl font-semibold text-title">
            {summary?.total_deficit || 0}
          </div>
        </div>

        {/* Tarjeta "Pedidos sin stock" - Ocupa toda la fila en mobile */}
        <div className="bg-card-bg border border-card-border rounded-lg p-4 col-span-2 md:col-span-1">
          <div className="flex items-center justify-between sm:mb-2">
            <span className="sm:text-lg font-medium text-xs">
              {t('report.ordersWithoutStock')}
            </span>
            <div className="relative text-lines">
              <OrdersNoStockIcon />
            </div>
          </div>
          <div className="text-lg sm:text-2xl font-semibold text-title">
            {summary?.total_orders_affected || 0}
          </div>
        </div>
      </div>
    </div>
  );
};
